import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Loader2, Package } from "lucide-react";
import { formatApiError, fmtINR } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Delhi","Chandigarh","Jammu and Kashmir","Ladakh","Puducherry"
];

export default function AddressForm() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    line1: "", line2: "", city: "", state: "Maharashtra", pincode: "", landmark: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/order/${token}`);
        setOrder(data);
        if (data.already_submitted) setDone(true);
      } catch (e) {
        toast.error("Invalid or expired link");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/order/${token}/address`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        address: {
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          landmark: form.landmark,
        },
      });
      setDone(true);
      toast.success("Address submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--mecha-red)]" />
      </div>
    );

  if (!order)
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="font-display text-3xl mb-3">LINK EXPIRED</div>
          <div className="text-zinc-400 text-sm">
            This link is invalid or has already been used. Contact the store.
          </div>
        </div>
      </div>
    );

  if (done)
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Toaster theme="dark" position="top-right" />
        <div className="max-w-md w-full card-brutal p-10 text-center">
          <CheckCircle2 className="h-14 w-14 mx-auto text-[color:var(--success)]" />
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)] mt-6">
            // CONFIRMED
          </div>
          <div className="font-display text-3xl mt-3">ADDRESS SAVED</div>
          <div className="text-zinc-400 text-sm mt-4">
            Thank you! Your Swagg Squad order{" "}
            <span className="font-mono text-white">{order.order_number}</span>{" "}
            is being packed and will ship shortly. You'll receive tracking on WhatsApp.
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />

      {/* Header */}
      <header className="border-b border-zinc-800 px-6 lg:px-12 py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white p-1 shrink-0">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_otaku-checkout/artifacts/lnjvv7pq_Yellow%20Black%20Brush%20Streetwear%20Brand%20Logo%282%29.png"
                alt="Swagg Squad"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-display text-xl leading-none">
                SWAGG<span className="text-[color:var(--mecha-red)]">.</span>SQUAD
              </div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mt-1">
                {order.store_name?.toUpperCase()}
              </div>
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 hidden sm:block">
            // DELIVERY FORM
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-10 lg:py-16">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // ORDER {order.order_number}
        </div>
        <h1 className="font-display text-4xl lg:text-6xl mt-3 leading-[0.95]">
          WHERE SHOULD<br />WE SHIP THIS?
        </h1>
        <div className="text-zinc-400 mt-4 max-w-xl">
          Your payment is confirmed. Fill your address and we'll dispatch your
          order within 24 hours. Tracking arrives on WhatsApp.
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 mt-10">
          <form onSubmit={submit} className="space-y-6">
            <section>
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-3">
                CONTACT
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="FULL NAME" testid="addr-name" required value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })} />
                <Input label="PHONE (+91)" testid="addr-phone" required value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })} placeholder="9876543210" />
              </div>
              <div className="mt-3">
                <Input label="EMAIL (OPTIONAL)" testid="addr-email" value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })} type="email" />
              </div>
            </section>

            <section>
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-3">
                DELIVERY ADDRESS
              </div>
              <div className="border-l-2 border-[color:var(--mecha-red)] pl-3 mb-3 text-xs text-zinc-400 leading-relaxed">
                <span className="text-white font-medium">Privacy:</span> Your details are used <strong>only</strong> to ship your order and to send you delivery updates on WhatsApp. We do not sell or share your information. You can request deletion any time by messaging us.
              </div>
              <div className="space-y-3">
                <Input label="ADDRESS LINE 1" testid="addr-line1" required value={form.line1}
                  onChange={(v) => setForm({ ...form, line1: v })} placeholder="House / Flat / Building" />
                <Input label="ADDRESS LINE 2 (OPTIONAL)" testid="addr-line2" value={form.line2}
                  onChange={(v) => setForm({ ...form, line2: v })} placeholder="Street / Area" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="CITY" testid="addr-city" required value={form.city}
                    onChange={(v) => setForm({ ...form, city: v })} />
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">STATE</label>
                    <select
                      data-testid="addr-state"
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                      className="w-full h-12 bg-zinc-950 border border-zinc-800 px-3 text-white focus:outline-none focus:border-[color:var(--mecha-red)]"
                    >
                      {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="PINCODE" testid="addr-pincode" required value={form.pincode}
                    onChange={(v) => setForm({ ...form, pincode: v })} pattern="[0-9]{6}" />
                  <Input label="LANDMARK (OPTIONAL)" testid="addr-landmark" value={form.landmark}
                    onChange={(v) => setForm({ ...form, landmark: v })} />
                </div>
              </div>
            </section>

            <button
              data-testid="addr-submit"
              type="submit"
              disabled={submitting}
              className="btn-brutal w-full h-14 bg-[color:var(--mecha-red)] text-white font-bold tracking-widest uppercase text-sm hover:bg-[color:var(--mecha-red-hover)] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & Ship My Order"}
            </button>
          </form>

          {/* Summary */}
          <aside className="card-brutal h-fit sticky top-4">
            <div className="p-5 border-b border-zinc-800 flex items-center gap-2">
              <Package className="h-4 w-4 text-[color:var(--mecha-red)]" />
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                ORDER SUMMARY
              </div>
            </div>
            <div className="p-5 space-y-3">
              {order.items?.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <div>
                    <div className="line-clamp-1">{i.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">
                      Qty {i.quantity}
                    </div>
                  </div>
                  <div className="font-mono">{fmtINR(i.price * i.quantity)}</div>
                </div>
              ))}
              <div className="flex justify-between items-end pt-4 border-t border-zinc-800">
                <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                  TOTAL PAID
                </span>
                <span className="font-display text-2xl text-[color:var(--success)]">
                  {fmtINR(order.total)}
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
        className="w-full h-12 bg-zinc-950 border border-zinc-800 px-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)] transition-colors"
      />
    </div>
  );
}
